import axios from '../axiosInstance';
import qs from 'qs';

/**
 * DataGrid-style query for enabled contracts (keyset pagination, sort, filter).
 * Backend: POST api/Contracts/queryEnabled
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledContracts({
    search,
    pageSize,
    cursor,
    sortBy,
    sortDir,
    filterModel,
    signal = null,
} = {}) {
    const body = {
        search: search ?? null,
        pageSize,
        cursor: cursor ?? null,
        sortBy: sortBy ?? 'description',
        sortDir: sortDir ?? 'asc',
        filterModel: filterModel ?? { items: [], logicOperator: 'and' },
    };

    const { data } = await axios.post('/Contracts/queryEnabled', body, { signal });
    return data;
}

// GET enabled, paginated (kept for route loader / lookups)
export async function getContractsEnabled({
    search = '',
    pageSize = 50,
    lastId = null,
    transTypeId = null,
    accountId = null,
    commodityId = null,
    searchFields = null,
    signal = null,
} = {}) {
    const params = { search, pageSize, lastId };
    if (transTypeId != null && transTypeId > 0) {
        params.transTypeId = transTypeId;
    }
    if (accountId != null && accountId > 0) {
        params.accountId = accountId;
    }
    if (commodityId != null && commodityId > 0) {
        params.commodityId = commodityId;
    }
    if (Array.isArray(searchFields) && searchFields.length) {
        params.searchFields = searchFields;
    }
    const { data } = await axios.get('/Contracts/getEnabled', {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat', skipNulls: true }),
        signal,
    });
    return data; // { items, lastId, hasMore }
}

export async function getContractById(id, signal = null) {
    const { data } = await axios.get(`/Contracts/${id}`, { signal });
    return data; // ContractDto (core fields)
}

// Optional: fetch related objects (lazy)
export async function getContractRelated(id, signal = null) {
    const { data } = await axios.get(`/Contracts/${id}/related`, { signal });
    return data; // ContractGetDto (only related minis)
}

export async function createContract(payload, signal = null) {
    const { data } = await axios.post('/Contracts', payload, { signal });
    return data; // { id }
}

export async function updateContract(id, payload, signal = null) {
    await axios.put(`/Contracts/${id}`, payload, { signal });
    return true;
}

export async function deleteContract(id, signal = null) {
    const { data } = await axios.delete(`/Contracts/${id}`, { signal });
    return data;
}

export async function restoreContract(id, signal = null) {
    await axios.post(`/Contracts/${id}/restore`, null, { signal });
    return true;
}

/**
 * Fetch enabled contracts by their IDs (for PPD filtering).
 * @param {number[]} ids - Array of contract IDs to fetch
 * @returns {Promise<{items: Array}>} Promise resolving to contracts data
 */
export async function getEnabledContractsByIds(ids, signal = null) {
    if (!ids || ids.length === 0) return { items: [] };
    const res = await axios.post(
        "/Contracts/byIdsEnabled",
        { filterModel: { items: [], logicOperator: 'and' } },
        {
            params: { ids },
            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
            signal,
        }
    );
    return res.data;
}

/**
 * Fetch lightweight contract data by IDs (only id + display fields for weighing console).
 * Returns minimal data: id, ref, description, erpCode for display purposes.
 * @param {number[]} ids - Array of contract IDs to fetch
 * @returns {Promise<Array>} Promise resolving to array of lightweight contract objects
 */
export async function getEnabledContractsByIdsLightweight(ids, signal = null) {
    if (!ids || ids.length === 0) return [];
    const res = await axios.post(
        "/Contracts/byIdsWeighingConsole",
        ids,
        { signal }
    );
    return res.data; // Returns array directly: [{ id, ref, description, erpCode }]
}
